import type { StockSummaryRow } from "@wms/domain";
import { formatQty } from "../api/format";

/**
 * ТЗ «Рабочие столы» 1.1, UXW-35…38 (31.10.2026): the pure part of «Остатки по товарам» — the short column names with
 * the formula behind each (the meaning of the server fields is not changed by the new words), quantities with their
 * unit, the applied filters said in words and the export with the former full names.
 */

/** The select value of «Принадлежность требует сверки»: rows whose stock fact has no owner (ADR 0014). */
export const OWNER_UNKNOWN = "__unknown__";

export type SummaryNumberField = "quantity" | "planned_out" | "planned_in" | "unreserved" | "available" | "excluded";

/** UXW-37: the short title, the former full title (kept in the hint and in the export) and what the number means. */
export const SUMMARY_COLUMNS: { field: SummaryNumberField | "locations"; title: string; former: string; help: string }[] = [
  {
    field: "quantity",
    title: "На складе",
    former: "На складе",
    help: "Весь остаток товара по выбранным складам и владельцу: годный, в резерве, в карантине и браке.",
  },
  {
    field: "planned_out",
    title: "В резерве",
    former: "Уже отложено",
    help: "Отложено под запланированные заказы и задания (резерв). Этот товар ещё лежит на складе.",
  },
  {
    field: "planned_in",
    title: "Ожидаем",
    former: "Ожидается приход",
    help: "Ожидается приход в адреса по открытым операциям. В «На складе» пока не входит.",
  },
  {
    field: "unreserved",
    title: "Без резерва",
    former: "Не зарезервировано",
    help: "«На складе» минус «В резерве» — просто разность, без учёта состояния, срока годности и адреса. Не путать с «К отбору».",
  },
  {
    field: "available",
    title: "К отбору",
    former: "Доступно к отбору",
    help: "Что отбор действительно возьмёт: свободный остаток в годном состоянии, срок не истёк, адрес в зоне хранения или отбора и разрешает отбор. Считает сервер по правилам планировщика.",
  },
  {
    field: "excluded",
    title: "Исключено",
    former: "Исключено из отбора",
    help: "Свободный остаток, который отбор не возьмёт: карантин, брак, просрочка, запрет адреса. Нажмите на число — откроются причины.",
  },
  {
    field: "locations",
    title: "Мест",
    former: "Мест хранения",
    help: "Сколько адресов (ячеек, коробов, паллет) держат этот товар.",
  },
];

/**
 * UXW-38: a quantity with its unit — «20 шт», «2,5 кг», technical zeros dropped, the server value not rounded beyond
 * its 3 digits. A missing value is «—» (unknown), never 0.
 */
export function qtyWithUnit(value: string | null | undefined, unit?: string | null): string {
  if (value === null || value === undefined || value === "") return "—";
  const text = formatQty(value);
  return unit ? `${text} ${unit}` : text;
}

/** A positive Decimal string without a float: «0.000» and «-0» are not positive, «0.001» is. */
export function positive(value: string | null | undefined): boolean {
  if (!value) return false;
  const text = value.trim();
  return !text.startsWith("-") && /[1-9]/.test(text);
}

export interface SummaryFilters {
  q: string;
  warehouseId?: string | null;
  owner?: string | null;
}

/** The query of `/stock/summary`: the owner of the fact, or the rows without one — never both. */
export function summaryParams(filters: SummaryFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.q) params.q = filters.q;
  if (filters.warehouseId) params.warehouse_id = filters.warehouseId;
  if (filters.owner === OWNER_UNKNOWN) params.owner_missing = "true";
  else if (filters.owner) params.owner_id = filters.owner;
  return params;
}

/** UXW-37 / section 3.1: the filters in words for the empty result — «Поиск: …», «Склад: …», «Владелец: …». */
export function appliedFilters(filters: SummaryFilters, names: { warehouse?: string; owner?: string }): string[] {
  const out: string[] = [];
  if (filters.q) out.push(`Поиск: «${filters.q}»`);
  out.push(`Склад: ${filters.warehouseId ? (names.warehouse ?? "выбранный") : "все доступные склады"}`);
  if (filters.owner === OWNER_UNKNOWN) out.push("Владелец: принадлежность требует сверки");
  else if (filters.owner) out.push(`Владелец: ${names.owner ?? "выбранный"}`);
  return out;
}

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? "" : String(value);
  return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * The export of the rows on the screen with the former full column names (UXW-37: the full names stay in the export);
 * the quantities go out exactly as the server sent them (Decimal strings), the unit in its own column.
 */
export function summaryCsv(rows: StockSummaryRow[]): string {
  const head = ["Код", "Наименование", "Артикул", "Ед.", ...SUMMARY_COLUMNS.map((c) => c.former), "Причины исключения"];
  const lines = rows.map((r) =>
    [
      r.storage_object_code,
      r.storage_object_name,
      r.storage_object_article ?? "",
      r.unit ?? "",
      r.quantity,
      r.planned_out,
      r.planned_in,
      r.unreserved,
      r.available,
      r.excluded,
      r.locations,
      r.excluded_reasons.join("; "),
    ]
      .map(csvCell)
      .join(";"),
  );
  return "﻿" + [head.map(csvCell).join(";"), ...lines].join("\n");
}
